import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Typography,
  Divider,
} from "@mui/material";
import { TaskerRewardOption } from "@t3/shared";
import { ElementType } from "react";
import { PointsBadge } from "./PointsBadge";
import { RewardText } from "./GradientRewardBox";

interface RewardOptionCardProps {
  option: TaskerRewardOption;
  isSelected: boolean;
  onSelect: (option: TaskerRewardOption) => void;
  viewOnly?: boolean;
  disabled?: boolean;
  programColor?: string;
}

export const RewardOptionCard = ({
  option,
  isSelected,
  onSelect,
  viewOnly = false,
  disabled = false,
  programColor,
}: RewardOptionCardProps) => {
  const CardWrapper: ElementType = viewOnly || disabled ? Box : CardActionArea;
  const cardWrapperProps =
    viewOnly || disabled ? {} : { onClick: () => onSelect(option) };

  // Safe fallback for programColor to prevent undefined in CSS
  const resolvedColor = programColor || "#E0E0E0";

  return (
    <Box
      className="reward-option"
      data-cypress="reward-option-card"
      sx={{
        flex: "1 1 100%",
        width: "100%",
      }}
    >
      <Card
        sx={{
          width: "100%",
          position: "relative",
          borderRadius: 2,
          backgroundColor: "white",
          border: isSelected ? "2px solid" : "2px solid transparent",
          borderColor: isSelected ? "success.main" : "transparent",
          boxShadow: isSelected
            ? "0 4px 12px rgba(76, 175, 80, 0.25)"
            : "0 2px 8px rgba(0, 0, 0, 0.08)",
          transition: "all 0.2s ease",
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          ...(!disabled && {
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
              transform: "translateY(-2px)",
            },
          }),
          "@keyframes glimmer": {
            "0%, 100%": {
              backgroundPosition: "200% 0",
            },
            "50%": {
              backgroundPosition: "-200% 0",
            },
          },
        }}
      >
        <CardWrapper {...cardWrapperProps}>
          <CardContent
            sx={{
              p: 0,
              pb: 0,
              position: "relative",
              background: `linear-gradient(135deg, ${resolvedColor} 0%, rgba(255, 255, 255, 1) 100%)`,
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background: `linear-gradient(90deg, ${resolvedColor} 0%, rgba(255, 255, 255, 0) 50%)`,
                opacity: 0.6,
              },
            }}
          >
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 },
                pr: { xs: 10, sm: 11 },
                position: "relative",
              }}
            >
              <PointsBadge amount={option.amount} />
              <Box sx={{ width: "100%" }}>
                <RewardText
                  title={option.label}
                  description={option.description}
                />
              </Box>
            </Box>
          </CardContent>

          {!viewOnly && (
            <>
              <Divider />
              <CardActions
                sx={{
                  p: { xs: 1.25, sm: 1.5 },
                  justifyContent: "center",
                  backgroundColor: isSelected ? "error.main" : "primary.main",
                  color: "white",
                  transition: "background-color 0.2s ease",
                  ...(!disabled && {
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "error.dark"
                        : "primary.dark",
                    },
                  }),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                    letterSpacing: "0.5px",
                  }}
                >
                  {isSelected ? "Remove Reward" : "Add Reward"}
                </Typography>
              </CardActions>
            </>
          )}
        </CardWrapper>
      </Card>
    </Box>
  );
};
